import { useState } from 'react'
import { useAuth } from '../../shared/context/AuthContext'
import { useFeatureToggleTenantsMutation, useSaveFeatureToggleLogMutation } from './useFeatureToggle'
import { useTenantToggleOperation } from './useTenantToggleOperation'
import { describeToggleResult } from './describeToggleResult'
import { parseTenantIdList, TENANT_ID_PATTERN } from './featureToggleUtils'

export function useFeatureTenantActions() {
  const { getUserLogin } = useAuth()
  const tenantToggle = useTenantToggleOperation()
  const tenantListMutation = useFeatureToggleTenantsMutation()
  const logMutation = useSaveFeatureToggleLogMutation()

  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [activeFeature, setActiveFeature] = useState(null)
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' })

  const [tenantDialogOpen, setTenantDialogOpen] = useState(false)
  const [tenantOperation, setTenantOperation] = useState('ADD')
  const [tenantTarget, setTenantTarget] = useState('')
  const [tenantReason, setTenantReason] = useState('')

  const [listDialogOpen, setListDialogOpen] = useState(false)
  const [tenantListInput, setTenantListInput] = useState('')
  const [listReason, setListReason] = useState('')

  function showToast(severity, message) {
    setSnackbar({ open: true, severity, message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  function openMenu(event, item) {
    setMenuAnchorEl(event.currentTarget)
    setActiveFeature(item)
  }

  function closeMenu() {
    setMenuAnchorEl(null)
  }

  function openTenantDialog(operation) {
    closeMenu()
    setTenantOperation(operation)
    setTenantTarget('')
    setTenantReason('')
    setTenantDialogOpen(true)
  }

  function openListDialog() {
    closeMenu()
    setTenantListInput('')
    setListReason('')
    setListDialogOpen(true)
  }

  async function submitTenantOperation() {
    setTenantDialogOpen(false)
    try {
      const response = await tenantToggle.run({
        feature: activeFeature.name,
        operation: tenantOperation,
        // Cliente pode colar email/tenantId com espaço acidental; trim antes
        // de enviar evita busca vazia (ver commit da1eb99).
        target: tenantTarget.trim(),
        reason: tenantReason,
      })
      const { severity, message } = describeToggleResult(response)
      showToast(severity, message)
    } catch {
      showToast('error', 'Ocorreu um problema. Verifique os dados.')
    } finally {
      setTenantTarget('')
      setTenantReason('')
      setActiveFeature(null)
    }
  }

  async function submitListOperation() {
    const tenantIds = parseTenantIdList(tenantListInput)
    const invalidTenantIds = tenantIds.filter((value) => !TENANT_ID_PATTERN.test(value))

    if (invalidTenantIds.length > 0) {
      showToast('error', `ID(s) de tenant inválido(s): ${invalidTenantIds.join(', ')}`)
      return
    }

    setListDialogOpen(false)
    try {
      const response = await tenantListMutation.mutateAsync({
        feature: activeFeature.name,
        tenantIds: tenantIds.map(Number),
      })
      let logFailed = false
      if (response.status === 204) {
        try {
          await logMutation.mutateAsync({
            who: getUserLogin(),
            feature: activeFeature.name,
            operation: 'ADD_LIST',
            tenantIds: tenantIds.map(Number),
            reason: listReason,
          })
        } catch {
          logFailed = true
        }
      }
      const { severity, message } = describeToggleResult({ ...response, logFailed })
      showToast(severity, message)
    } catch {
      showToast('error', 'Ocorreu um problema. Verifique os dados.')
    } finally {
      setTenantListInput('')
      setListReason('')
      setActiveFeature(null)
    }
  }

  return {
    activeFeature,
    openMenu,
    closeMenu,
    showToast,
    menuProps: {
      anchorEl: menuAnchorEl,
      onClose: closeMenu,
      onAddTenant: () => openTenantDialog('ADD'),
      onRemoveTenant: () => openTenantDialog('REMOVE'),
      onAddList: openListDialog,
    },
    tenantDialogProps: {
      open: tenantDialogOpen,
      operation: tenantOperation,
      featureName: activeFeature?.name,
      target: tenantTarget,
      reason: tenantReason,
      onTargetChange: setTenantTarget,
      onReasonChange: setTenantReason,
      onCancel: () => setTenantDialogOpen(false),
      onSubmit: submitTenantOperation,
    },
    listDialogProps: {
      open: listDialogOpen,
      featureName: activeFeature?.name,
      tenantListInput,
      reason: listReason,
      onTenantListInputChange: setTenantListInput,
      onReasonChange: setListReason,
      onCancel: () => setListDialogOpen(false),
      onSubmit: submitListOperation,
    },
    snackbar,
    handleSnackbarClose,
    busy: tenantToggle.isPending || tenantListMutation.isPending || logMutation.isPending,
  }
}
